import { Box, IconButton, LinearProgress, Typography } from '@mui/material'
import { InfoCircleIcon, Upload01Icon, XCloseIcon } from '@untitled-theme/icons-react'
import { useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { useSubmitISFPData } from '@/shared/api'
import Button from '@/shared/components/Button'
import LoadingIndicator from '@/shared/components/LoadingIndicator'
import TextButton from '@/shared/components/TextButton'
import { useAssessment } from '@/shared/hooks'
import type { ISFPExtractedData } from '@/shared/types/isfp.types'
import { UploadAbortError } from '@/shared/utils/http-client'

import { useUploadISFPFileWithProgress } from '../api/isfp-upload.queries'
import isfpDocument from '../assets/upload-image.svg'
import ISFPUploadForm from '../components/ISFPUploadForm'
import OptionalFields from '../components/OptionalFields'
import { useISFPFormController } from '../hooks'
import type { MetaData } from '../types'
import { mapExtractedDataToForm } from '../utils/mapExtractedDataToForm'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

const ISFPUploadPage = () => {
  const { buildingUuid } = useParams<{ buildingUuid: string }>()
  const navigate = useNavigate()

  // Listen to Firestore assessment document
  const { status, loading: assessmentLoading } = useAssessment(buildingUuid)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const pdfContainerRef = useRef<HTMLDivElement>(null)
  const abortUploadRef = useRef<(() => void) | null>(null)

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(undefined)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [isAnalyzed, setIsAnalyzed] = useState(false)
  const [fileUrl, setFileUrl] = useState<string | null>(null)
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageWidth, setPageWidth] = useState<number>(600)
  const [wasProcessing, setWasProcessing] = useState(false)
  const [showOptionalFields, setShowOptionalFields] = useState(false)
  const [showError, setShowError] = useState(false)
  const [metadata, setMetadata] = useState<MetaData | undefined>(undefined)

  // Initialize form controller
  const formController = useISFPFormController()

  // Calculate PDF width based on container size
  useEffect(() => {
    const updateWidth = () => {
      if (pdfContainerRef.current) {
        const containerWidth = pdfContainerRef.current.offsetWidth
        // Subtract padding (6px * 2 = 12px)
        setPageWidth(containerWidth - 12)
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [pdfContainerRef.current?.offsetWidth])

  // Track assessment processing status and show optional fields when processed
  useEffect(() => {
    if (status === 'processing') {
      setWasProcessing(true)
    }

    if (status === 'processed' && wasProcessing) {
      setShowOptionalFields(true)
    }

    if (!!status && status !== 'failure') {
      setShowError(true)
    }
  }, [status, wasProcessing])

  useEffect(() => {
    if (status === 'failure' && showError) {
      setShowError(false)
      toast.error(
        'Fehler bei der Verarbeitung der iSFP-Daten. Bitte senden Sie die Daten nach einiger Zeit erneut.',
        {
          position: 'top-right',
          autoClose: 5000,
        }
      )
    }
  }, [status, showError])

  const handleUploadClick = () => {
    if (!selectedFile) {
      fileInputRef.current?.click()
    }
  }

  const handleCancel = () => {
    setSelectedFile(null)
    setIsAnalyzing(false)
    setIsAnalyzed(false)
    setUploadProgress(undefined)
    abortUploadRef.current = null

    // Reset the form data
    formController.reset()

    if (fileUrl) {
      URL.revokeObjectURL(fileUrl)
      setFileUrl(null)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const uploadMutation = useUploadISFPFileWithProgress({
    onSuccess: async response => {
      setUploadProgress(100)
      setIsAnalyzing(true)
      setIsAnalyzed(true)
      abortUploadRef.current = null

      // Store metadata for PDF page navigation
      if (response.meta) {
        setMetadata(response.meta)
      }

      // Prefill form with extracted data from API response
      if (response.data) {
        const formData = mapExtractedDataToForm(response.data)
        formController.prefillForm(formData)

        // Trigger validation after prefilling
        await formController.trigger()
      }

      toast.success('Datei erfolgreich hochgeladen und verarbeitet!', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
    },
    onError: error => {
      handleCancel()

      // Don't show error toast if upload was aborted by user
      if (error instanceof UploadAbortError) {
        return
      }

      toast.error(`Fehler beim Hochladen der Datei: ${error.message || error}`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
    },
  })

  const handleClearFile = (event: React.MouseEvent) => {
    event.stopPropagation()

    // Abort the ongoing upload if it exists
    if (abortUploadRef.current) {
      abortUploadRef.current()
      abortUploadRef.current = null
    }

    uploadMutation.reset()
    handleCancel()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setIsAnalyzing(true)
      setIsAnalyzed(false)
      setUploadProgress(undefined)

      // Create URL for PDF preview
      const url = URL.createObjectURL(file)
      setFileUrl(url)

      // Start upload with progress tracking
      uploadMutation.mutate({
        file,
        buildingUuid: buildingUuid!,
        onProgress: progress => {
          setUploadProgress(progress)
        },
        onAbortReady: abort => {
          abortUploadRef.current = abort
        },
      })
    }
  }

  const [isSubmitting, setIsSubmitting] = useState(false)

  const submitMutation = useSubmitISFPData({
    onSuccess: () => {
      setIsSubmitting(false)
      toast.success('Formulardaten erfolgreich gespeichert!', {
        position: 'top-right',
        autoClose: 3000,
      })
    },
    onError: error => {
      setIsSubmitting(false)
      toast.error(`Fehler beim Speichern der Daten: ${error.message || error}`, {
        position: 'top-right',
        autoClose: 5000,
      })
    },
  })

  const handleFormSubmit = (data: ISFPExtractedData) => {
    setIsSubmitting(true)
    submitMutation.mutate({
      buildingUuid: buildingUuid!,
      data,
    })
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  const handleOptionalFieldsComplete = () => {
    navigate(`/${buildingUuid}`)
  }

  // Scroll PDF to specific page
  const scrollToPage = (pageNumber: number) => {
    if (!pdfContainerRef.current || !pageNumber || pageNumber < 1) return

    // Find all page elements (react-pdf renders them with .react-pdf__Page class)
    const pageElements = pdfContainerRef.current.querySelectorAll('.react-pdf__Page')

    if (pageElements.length === 0) return

    // Get the target page (pageNumber is 1-indexed)
    const targetPageIndex = pageNumber - 1
    const targetPage = pageElements[targetPageIndex]

    if (targetPage) {
      // Scroll the target page into view within the PDF container
      targetPage.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Show optional fields screen after processing is complete
  if (showOptionalFields) {
    return (
      <OptionalFields formController={formController} onComplete={handleOptionalFieldsComplete} />
    )
  }

  // Show processing screen if assessment is loading or being processed
  if (assessmentLoading || status === 'processing') {
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          width: '100vw',
          backgroundColor: '#ffffff',
          padding: '24px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '24px',
            textAlign: 'center',
          }}
        >
          <LoadingIndicator size={150} />
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '18px',
            }}
          >
            Daten werden verarbeitet
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '14px',
              color: '#74777F',
            }}
          >
            In wenigen Augenblicken können Sie mit der Energieberatung beginnen
          </Typography>
        </Box>
      </Box>
    )
  }

  // Show analyzing/form view when upload is in progress or complete
  if (isAnalyzing && fileUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          height: '100vh',
          width: '100vw',
          backgroundColor: '#ffffff',
          padding: '60px 40px 0 40px',
          overflow: 'hidden',
        }}
      >
        {/* Title at top left */}
        <Box
          sx={{
            flexShrink: 0,
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontWeight: 600,
              fontSize: '20px',
            }}
          >
            iSFP Umsetzungshilfe hinzufügen
          </Typography>
        </Box>

        {Object.keys(formController.errors).length > 0 && (
          <Box
            onClick={() => formController.scrollToFirstError()}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '16px',
              padding: '16px',
              backgroundColor: '#F9DEDC',
              borderRadius: '18px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#F7D3D0',
              },
            }}
          >
            <InfoCircleIcon style={{ width: 24, height: 24, color: '#740000', flexShrink: 0 }} />
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                color: '#740000',
              }}
            >
              Bitte ergänzen Sie die fehlenden Daten um fortzufahren
            </Typography>
          </Box>
        )}

        {/* Main content area with two columns */}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            gap: '24px',
            overflow: 'hidden',
          }}
        >
          {/* Left side - PDF Viewer (hidden on mobile) */}
          <Box
            ref={pdfContainerRef}
            sx={{
              flex: 1,
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              backgroundColor: '#F5F5F7',
              borderRadius: '8px',
              padding: '24px 0px',
              overflow: 'auto',
              alignItems: 'center',
            }}
          >
            <Document
              file={fileUrl}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <LoadingIndicator />
                </Box>
              }
            >
              {numPages &&
                Array.from(new Array(numPages), (_el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={pageWidth}
                    loading={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          padding: '20px',
                        }}
                      >
                        <LoadingIndicator size={30} />
                      </Box>
                    }
                  />
                ))}
            </Document>
          </Box>

          {/* Right side - Loading Message or Form */}
          {isAnalyzed && !isSubmitting ? (
            <ISFPUploadForm
              controller={formController}
              metadata={metadata}
              onFieldFocus={scrollToPage}
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '24px',
                overflow: 'auto',
                textAlign: 'center',
              }}
            >
              <LoadingIndicator size={150} />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '18px',
                }}
              >
                {isSubmitting ? 'Daten werden gespeichert' : 'Dokument wird analysiert'}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#74777F',
                }}
              >
                Dies kann einige Momente dauern.
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '12px',
                  color: '#74777F',
                  fontStyle: 'italic',
                }}
              >
                Bitte schließen Sie dieses Fenster nicht.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Bottom Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: '24px',
            paddingBottom: '24px',
            flexShrink: 0,
          }}
        >
          <TextButton
            onClick={handleClearFile}
            height="56px"
            sx={{ flex: 1 }}
            disabled={isSubmitting}
          >
            Abbrechen
          </TextButton>
          <Button
            disabled={!isAnalyzed || isSubmitting}
            onClick={formController.handleSubmit(
              handleFormSubmit,
              // onError callback - called when validation fails
              () => formController.scrollToFirstError()
            )}
            height="56px"
            sx={{ flex: 1 }}
          >
            Übernehmen
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        width: '100vw',
        backgroundColor: '#ffffff',
        padding: '24px',
      }}
    >
      <Box sx={{ maxWidth: '660px', width: '100%' }}>
        <Typography
          component="h1"
          sx={{
            fontWeight: 600,
            fontSize: '20px',
            marginBottom: '16px',
          }}
        >
          iSFP Umsetzungshilfe hinzufügen
        </Typography>

        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            marginBottom: '40px',
            color: '#74777F',
          }}
        >
          Fügen Sie das PDF der Technischen Umsetzungshilfe hinzu, um die Energieberatung
          durchführen zu können.{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            Laden Sie nur das originale PDF hoch, keine Scans oder Fotos.
          </Box>
        </Typography>

        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            marginBottom: '8px',
            color: '#464A54',
          }}
        >
          Technische Umsetzungshilfe
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        <Box
          onClick={handleUploadClick}
          sx={{
            height: '102px',
            backgroundColor: '#FAFAFB',
            borderRadius: '8px',
            border: selectedFile ? '1px solid #E1E1E4' : '1px dashed #E1E1E4',
            borderSpacing: '2px 2px',
            display: 'flex',
            alignItems: selectedFile ? 'flex-start' : 'center',
            justifyContent: selectedFile ? 'space-between' : 'center',
            gap: '16px',
            padding: '0 16px',
            paddingTop: selectedFile ? '14px' : '0',
            cursor: selectedFile ? 'default' : 'pointer',
            position: 'relative',
            '&:hover': {
              backgroundColor: selectedFile ? '#FAFAFB' : '#F5F5F5',
            },
          }}
        >
          {selectedFile ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '18px',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '12px',
                    }}
                  >
                    {selectedFile.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '11px',
                      color: '#74777F',
                    }}
                  >
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>

                <IconButton
                  onClick={handleClearFile}
                  sx={{
                    padding: '8px',
                    margin: '-8px',
                    minWidth: 'auto',
                  }}
                >
                  <XCloseIcon style={{ width: 24, height: 24, color: '#464A54' }} />
                </IconButton>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                }}
              >
                <LinearProgress
                  variant={uploadProgress === undefined ? 'indeterminate' : 'determinate'}
                  value={uploadProgress}
                  sx={{
                    flex: 1,
                    height: '8px',
                    borderRadius: '4px',
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '11px',
                    color: '#464A54',
                    minWidth: '30px',
                    textAlign: 'right',
                  }}
                >
                  {uploadProgress !== undefined ? `${Math.round(uploadProgress)} %` : '-- %'}
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Box
                component="img"
                src={isfpDocument}
                alt="iSFP Document"
                sx={{ width: 40, height: 40 }}
              />

              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#464A54',
                }}
              >
                Technische Umsetzungshilfe hochladen
              </Typography>

              <Upload01Icon style={{ width: 24, height: 24, color: '#464A54' }} />
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default ISFPUploadPage
